import { create } from 'zustand'

/** An export this browser started and waits on until the worker finishes it. */
export interface WatchedExport {
  exportId: string
  /** The table or list it exports (exportScopeKey), whose Recent Exports it updates. */
  scopeKey: string
  fileName: string
  /** The page it was started from: it downloads on its own while you're still there. */
  startedOnPath: string
  /** Print: when it's ready, the print dialog opens for it instead of a download. */
  print?: boolean
  /** Share: when it's ready, the device's share sheet opens with it instead. */
  share?: boolean
}

interface ExportWatchState {
  exports: WatchedExport[]
  /**
   * Exports being queued, by scope key (how many): from the click until the
   * server answers, so a menu's dot turns amber the moment its pop-up closes.
   */
  starting: Record<string, number>
  begin: (scopeKey: string) => void
  end: (scopeKey: string) => void
  /**
   * Prints under way, by scope key (how many): from the click until the
   * print dialog opens (or the print fails), so the menu's print button
   * spins and the menu closes once the dialog is up.
   */
  printing: Record<string, number>
  beginPrint: (scopeKey: string) => void
  endPrint: (scopeKey: string) => void
  /** The same for shares: until the share sheet opens (or the share fails). */
  sharing: Record<string, number>
  beginShare: (scopeKey: string) => void
  endShare: (scopeKey: string) => void
  watch: (watched: WatchedExport) => void
  /** Stops waiting; true when it was still being waited on. */
  finish: (exportId: string) => boolean
}

/**
 * Exports started in this browser, waited on by `ExportWatcher` (in the app
 * shell, so leaving the page doesn't lose them) until they finish.
 */
export const useExportWatchStore = create<ExportWatchState>()((set, get) => ({
  exports: [],
  starting: {},
  begin: (scopeKey) => set((state) => ({ starting: counted(state.starting, scopeKey, 1) })),
  end: (scopeKey) => set((state) => ({ starting: counted(state.starting, scopeKey, -1) })),
  printing: {},
  beginPrint: (scopeKey) => set((state) => ({ printing: counted(state.printing, scopeKey, 1) })),
  endPrint: (scopeKey) => set((state) => ({ printing: counted(state.printing, scopeKey, -1) })),
  sharing: {},
  beginShare: (scopeKey) => set((state) => ({ sharing: counted(state.sharing, scopeKey, 1) })),
  endShare: (scopeKey) => set((state) => ({ sharing: counted(state.sharing, scopeKey, -1) })),
  watch: (watched) =>
    set((state) => ({
      exports: [
        ...state.exports.filter((candidate) => candidate.exportId !== watched.exportId),
        watched,
      ],
    })),
  finish: (exportId) => {
    if (!get().exports.some((watched) => watched.exportId === exportId)) return false
    set((state) => ({
      exports: state.exports.filter((watched) => watched.exportId !== exportId),
    }))
    return true
  },
}))

/** `counts` with `scopeKey`'s count moved by `by`, dropped at zero. */
function counted(counts: Record<string, number>, scopeKey: string, by: number) {
  const next = { ...counts }
  const left = (counts[scopeKey] ?? 0) + by
  if (left > 0) next[scopeKey] = left
  else delete next[scopeKey]
  return next
}
